import React, { useState } from 'react';
import { motion } from 'framer-motion';

export default function WaitlistForm() {
  const [userType, setUserType] = useState('parent');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    const apiKey = import.meta.env.VITE_BREVO_API_KEY;
    const senderEmail = import.meta.env.VITE_SENDER_EMAIL;
    const receiverEmail = import.meta.env.VITE_RECEIVER_EMAIL;

    setStatus('submitting');
    setErrorMessage('');

    try {
      // 1. Add to Brevo Contacts (CRM)
      const contactResponse = await fetch('https://api.brevo.com/v3/contacts', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'content-type': 'application/json',
          'api-key': apiKey
        },
        body: JSON.stringify({
          email: email.toLowerCase().trim(),
          attributes: {
            USER_TYPE: userType === 'parent' ? 'Parent' : 'School',
            SIGNUP_SOURCE: 'Waitlist'
          },
          updateEnabled: true
        })
      });

      // 2. Send Notification Email to YOU
      const roleLabel = userType === 'parent' ? 'PARENT/COMMUNITY' : 'SCHOOL/DISTRICT';
      
      const emailResponse = await fetch('https://api.brevo.com/v3/smtp/email', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'content-type': 'application/json',
          'api-key': apiKey
        },
        body: JSON.stringify({
          sender: { name: "Veric Waitlist", email: senderEmail },
          to: [{ email: receiverEmail }],
          subject: `New Signup: ${roleLabel}`,
          htmlContent: `
            <div style="font-family: sans-serif; padding: 20px; border: 1px solid #eee;">
              <h2 style="color: #1A1A2E;">New Waitlist Entry</h2>
              <p><strong>User Email:</strong> ${email}</p>
              <p><strong>Interested As:</strong> ${roleLabel}</p>
              <p><strong>Timestamp:</strong> ${new Date().toLocaleString()}</p>
            </div>
          `
        })
      });

      if (!emailResponse.ok) {
        const errorData = await emailResponse.json();
        console.error('Email API Error:', errorData);
        // We don't stop the success message for the user, but we log the error for you
      }

      setStatus('success');
      setEmail('');
    } catch (err) {
      console.error('Submission Error:', err);
      setStatus('error');
      setErrorMessage('Verification error. Please check sender settings.');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  if (status === 'success') {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-lg mx-auto py-12 px-8 bg-gray-50 rounded-xl text-center border border-gray-100"
      >
        <h3 className="text-2xl font-bold text-navy mb-2">Thank you.</h3>
        <p className="text-navy/60 font-medium">We've added your email to the waitlist.</p>
        <button 
          onClick={() => setStatus('idle')}
          className="mt-6 text-sm font-bold text-deep-blue hover:underline"
        >
          Add another
        </button>
      </motion.div>
    );
  }

  return (
    <section id="waitlist" className="w-full py-24 md:py-40 border-t border-gray-100">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-extrabold text-navy tracking-tight">
            Be part of it from the beginning.
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
            <label className="flex items-center gap-3 cursor-pointer group">
              <div className="relative flex items-center justify-center w-5 h-5">
                <input 
                  type="radio" 
                  name="userType" 
                  checked={userType === 'parent'}
                  onChange={() => setUserType('parent')}
                  className="peer sr-only" 
                />
                <div className="w-5 h-5 border-2 border-navy/20 rounded-full peer-checked:border-deep-blue transition-all"></div>
                <div className={`absolute w-2.5 h-2.5 bg-deep-blue rounded-full transition-transform duration-200 ${userType === 'parent' ? 'scale-100' : 'scale-0'}`}></div>
              </div>
              <span className={`text-base font-semibold transition-colors ${userType === 'parent' ? 'text-navy' : 'text-navy/40 group-hover:text-navy/60'}`}>
                I'm a parent or community member
              </span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer group">
              <div className="relative flex items-center justify-center w-5 h-5">
                <input 
                  type="radio" 
                  name="userType" 
                  checked={userType === 'school'}
                  onChange={() => setUserType('school')}
                  className="peer sr-only" 
                />
                <div className="w-5 h-5 border-2 border-navy/20 rounded-full peer-checked:border-deep-blue transition-all"></div>
                <div className={`absolute w-2.5 h-2.5 bg-deep-blue rounded-full transition-transform duration-200 ${userType === 'school' ? 'scale-100' : 'scale-0'}`}></div>
              </div>
              <span className={`text-base font-semibold transition-colors ${userType === 'school' ? 'text-navy' : 'text-navy/40 group-hover:text-navy/60'}`}>
                I represent a school or district
              </span>
            </label>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <input 
                type="email" 
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address" 
                className="flex-grow px-6 py-4 text-lg bg-white border-2 border-navy/10 rounded-lg focus:outline-none focus:border-deep-blue transition-all placeholder:text-navy/30 font-medium"
              />
              <button 
                type="submit"
                disabled={status === 'submitting'}
                className="px-10 py-4 bg-navy text-white text-lg font-bold rounded-lg hover:bg-deep-blue transition-all active:scale-[0.98] disabled:opacity-70 flex justify-center items-center h-[64px] min-w-[160px]"
              >
                {status === 'submitting' ? (
                  <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  'Join Waitlist'
                )}
              </button>
            </div>
            {status === 'error' && (
              <p className="text-sm text-red-500 text-center font-bold">{errorMessage}</p>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}